﻿import React, { useMemo, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import { str_isEmpty } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';
import { Popup_Edit_Projects_Props, Project } from '@Util/Types';

import Popup from '@Elements/Popup/Popup';
import Input_With_Label from '@Elements/FormElements/Input_With_Label';
import Checkbox from '@Elements/FormElements/Checkbox';
import Submit_Button from '@Elements/FormElements/Submit_Button';

export default function Edit_Projects_Popup(props: Popup_Edit_Projects_Props): JSX.Element {
    const { t } = useTranslation();
    const { customers, fetch_customers, set_status_update }: context_type = useContext(Context);

    const [name, set_name] = useState<string>("");
    const [is_archived, set_is_archived] = useState<boolean>(false);
    const [name_error, set_name_error] = useState<string | null>(null);
    const [saving, set_saving] = useState<boolean>(false);

    const chosen_project: Project | null = useMemo(() => {
        for (const customer of customers) {
            const project = customer.projects.find(p => p.id === props.chosen_projects_id);
            if (project) return project;
        }
        return null;
    }, [customers, props.chosen_projects_id]);

    useEffect(() => {
        if (chosen_project) {
            set_name(chosen_project.name);
            set_is_archived(chosen_project.isArchived);
        }
    }, [chosen_project]);

    const handle_save_project_name = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;

        if (str_isEmpty(name)) {
            set_name_error(t('Error.ProjectNameEmpty'));
            return;
        }

        set_saving(true);
        set_name_error(null);

        try {
            const api_call = await Api.callApi(`${apiBase}/UpdateProject`, {
                id: chosen_project?.id,
                name: name.trim(),
                isArchived: is_archived
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                props.set_open(false);
                set_name("");
                set_is_archived(false);
                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });
                fetch_customers();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        } finally {
            set_saving(false);
        }
    }, [chosen_project, name, is_archived, saving, fetch_customers, set_status_update, t, props]);

    return (
        <Popup
            open={props.open}
            set_open={props.set_open}
            title={`${t('Text.Edit')}: ${chosen_project?.name}`}
            disable_close_action={false}
        >
            <form onSubmit={handle_save_project_name}>
                <Input_With_Label
                    type="text"
                    id="edit_project_name"
                    label={t('Label.Name')}
                    value={name}
                    onChange={(e) => set_name(e.target.value)}
                    error={Boolean(name_error)}
                />
                <Checkbox
                    id={`edit_project_is_archived${chosen_project?.id}`}
                    checked={is_archived}
                    onChange={(e) => set_is_archived(e.target.checked)}
                    label={t('Label.IsArchived')}
                    error={false}
                />
                <Submit_Button
                    text={t('Button.Save')}
                    disabled={saving}
                    loading={saving}
                />
                {name_error && <div className="error-message">{name_error}</div>}
            </form>
        </Popup>
    );
}